import { useEffect, useState } from 'react';
import DokladSkenPanel from './DokladSkenPanel';
import DokladZauctovaniPanel from '../../components/doklady/DokladZauctovaniPanel';
import DokladHistoriePanel from '../../components/doklady/DokladHistoriePanel';

/**
 * Třetí sloupec modulu Doklady — sken · zaúčtování · historie.
 *
 * Tři KARTY, ne tři pruhy v jedné: každá si pak řekne o svou výšku. Sken si
 * bere zbytek sloupce (`flex: 1`), zaúčtování a historie zaberou, co potřebují.
 * Na vysoké obrazovce je tedy sken velký a všechno se vejde; na nízké se sken
 * stáhne na svoje minimum a sloupec začne scrollovat — dřív měla celá trojice
 * pevnou výšku obsahu a sken se mačkal do zbytku bez ohledu na rozlišení.
 *
 * Data se tahají JEDNOU pro celý sloupec a jdou do všech tří karet; dřív si
 * je bral panel skenu a ostatní jely jako jeho součásti.
 */
export default function DokladPanelSloupec({
    dokladId, cardStyle, isDark, mutedColor, isAdmin = false, onNahrat, onZmena,
    /** `sken` = jen karta skenu (úzká obrazovka, kde zbytek nese záložka detailu). */
    cast = 'vse',
    kompaktni = false,
}) {
    // Data si drží ID dokladu, ke kterému patří, a karty dostanou jen ta, co
    // sedí s právě vybraným dokladem.
    //
    // Sloupec se mezi doklady nepřemountovává (nemá `key`), takže po přepnutí
    // na chvíli platilo obojí najednou: `dokladId` už nové, `data` ještě
    // původní. Karta skenu z toho poskládala odkaz na přílohu — nové ID
    // dokladu, staré ID přílohy — a server na takovou kombinaci vrací 404.
    // Náhled pak hlásil „Soubor přílohy na serveru chybí", i když soubor na
    // disku normálně ležel. V access logu to je vidět jako dvojice: jeden 404
    // a hned za ním 200 na správnou přílohu.
    const [nactene, setNactene] = useState(null);   // { id, doklad }
    const [verze, setVerze] = useState(0);
    const data = nactene?.id === dokladId ? nactene.doklad : null;

    useEffect(() => {
        if (!dokladId) { setNactene(null); return; }
        let zije = true;
        fetch(`/api/finance/doklady/${dokladId}/detail`, { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (zije) setNactene({ id: dokladId, doklad: d?.doklad ?? null }); })
            .catch(() => {});
        return () => { zije = false; };
    }, [dokladId, verze]);

    /** Uloží jedno pole zaúčtování a dorovná sloupec i zbytek modulu. */
    const ulozPole = async (patch) => {
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json', 'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                },
                body: JSON.stringify(patch),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.message || 'Uložení selhalo'); return false; }
            setVerze(v => v + 1);
            onZmena?.();
            return true;
        } catch { alert('Síťová chyba'); return false; }
    };

    const sken = (
        <DokladSkenPanel doklad={data} dokladId={dokladId} cardStyle={cardStyle} isDark={isDark}
            mutedColor={mutedColor} onNahrat={onNahrat} kompaktni={kompaktni} />
    );

    if (cast === 'sken') return sken;

    return (
        <>
            {/* Sken bere zbytek výšky sloupce — proto `flex: 1` a minimum, pod
                které se nesmí zmáčknout. Bez minima by se na nízké obrazovce
                scvrkl na pruh a náhled by nebyl k ničemu. */}
            <div style={{ flex: '1 1 auto', minHeight: 220, display: 'flex', flexDirection: 'column' }}>
                {sken}
            </div>

            {data && (
                <div style={{ flexShrink: 0 }}>
                    <DokladZauctovaniPanel doklad={data} isAdmin={isAdmin} isDark={isDark}
                        mutedColor={mutedColor} cardStyle={cardStyle} onSave={ulozPole}
                        onZmena={() => { setVerze(v => v + 1); onZmena?.(); }} />
                </div>
            )}

            {data && (data.workflow_log || []).length > 0 && (
                <div style={{ flexShrink: 0 }}>
                    <DokladHistoriePanel log={data.workflow_log} cardStyle={cardStyle}
                        isDark={isDark} mutedColor={mutedColor} />
                </div>
            )}
        </>
    );
}
